import { API, HOST } from '@/utils/http'

/**
 * Get all reports for an organization visible to the current user
 * @param {number} organizationId - The organization ID
 * @returns {Promise<Object>} List of reports (latest version of each)
 */
export async function getReports(organizationId) {
  try {
    const response = await API.get(`${HOST}/organizations/${organizationId}/reports`)
    return response.data
  } catch (error) {
    console.error('Error fetching reports:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch reports')
  }
}

/**
 * Get a single report by UUID
 * @param {number} orgId - The organization ID
 * @param {string} reportUuid - The report UUID
 * @param {number|null} version - Optional specific version to retrieve
 * @returns {Promise<Object>} The report data with available_versions
 */
export async function getReport(orgId, reportUuid, version = null) {
  try {
    let url = `${HOST}/organizations/${orgId}/reports/${reportUuid}`
    if (version !== null) {
      url += `?version=${version}`
    }
    const response = await API.get(url)
    return response.data
  } catch (error) {
    console.error('Error fetching report:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch report')
  }
}

/**
 * Get all available versions for a report
 * @param {number} orgId - The organization ID
 * @param {string} reportUuid - The report UUID
 * @returns {Promise<Object>} Versions info { report_uuid, versions, latest_version }
 */
export async function getReportVersions(orgId, reportUuid) {
  try {
    const response = await API.get(`${HOST}/organizations/${orgId}/reports/${reportUuid}/versions`)
    return response.data
  } catch (error) {
    console.error('Error fetching report versions:', error)
    throw new Error(error.response?.data?.detail || 'Failed to fetch report versions')
  }
}

/**
 * Delete a report (soft delete)
 * @param {number} orgId - The organization ID
 * @param {string} reportUuid - The report UUID
 * @returns {Promise<void>}
 */
export async function deleteReport(orgId, reportUuid) {
  try {
    await API.delete(`${HOST}/organizations/${orgId}/reports/${reportUuid}`)
  } catch (error) {
    console.error('Error deleting report:', error)
    throw new Error(error.response?.data?.detail || 'Failed to delete report')
  }
}

/**
 * Update report settings
 * @param {number} orgId - The organization ID
 * @param {string} reportUuid - The report UUID
 * @param {Object} settings - The settings to update
 * @param {boolean} [settings.is_public] - Whether the report is public
 * @returns {Promise<Object>} The updated report
 */
export async function updateReportSettings(orgId, reportUuid, settings) {
  try {
    const response = await API.patch(
      `${HOST}/organizations/${orgId}/reports/${reportUuid}/settings`,
      settings
    )
    return response.data
  } catch (error) {
    console.error('Error updating report settings:', error)
    throw new Error(error.response?.data?.detail || 'Failed to update report settings')
  }
}
